import {
  AlertTriangle,
  BadgeCheck,
  BriefcaseIcon,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Euro,
  FilePen,
  FileUp,
  Globe,
  Hash,
  Home,
  Key,
  Mail,
  MapPin,
  Maximize2,
  PartyPopper,
  Percent,
  Phone,
  Search,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Store,
  User,
  Users,
} from 'lucide-react';
import type {
  ConditionalVisibility,
  FieldShortcut,
  FormSection,
  FormSectionsWithShortcuts,
  KorintModulesProductsConfig,
} from 'korint-sdk/react';
import {
  ManualQuoteComponentDto,
  ManualQuotePercentageDto,
  policyControllerPushPolicyEvent,
  PolicyDto,
} from 'korint-sdk';
import { centsToEuros, eurosToCents } from '@folio/utils';
import { ContactKey } from '@folio/utils/customerDisplayName';
import { z, type ZodTypeAny } from 'zod';
import { IncludedPerilsMRP } from '../custom-pages/IncludedPerilsMRP';

const showWhenCompanyExists = [
  {
    fieldName: 'isCompanyBeingCreated',
    condition: 'equals',
    value: 'false',
  },
] as const satisfies ConditionalVisibility[];

const showWhenCompanyBeingCreated = [
  {
    fieldName: 'isCompanyBeingCreated',
    condition: 'equals',
    value: 'true',
  },
] as const satisfies ConditionalVisibility[];

const companyFunnelSections: FormSectionsWithShortcuts = [
  {
    title: "Informations de l'entreprise",
    fields: [
      { name: 'isCompanyBeingCreated', display: 'toggle' as const },
      {
        name: 'companySiret',
        type: 'company',
        displayCompanyInformation: true,
        icon: Search,
        apiDataAccessor: apiData =>
          apiData.customFields?.companySiret
            ? {
                siret: apiData?.customFields?.companySiret,
                name: apiData?.customFields?.companyName,
                location: {
                  postCode: apiData?.customFields?.companyPostalCode,
                  city: apiData?.customFields?.companyCity,
                  address: apiData?.customFields?.companyAddress,
                },
                nafCode: apiData?.customFields?.companyNafCode,
              }
            : null,
        formDataAccessor: 'companySiret.siret',
        showWhen: showWhenCompanyExists,
      },
      {
        name: 'companyName',
        DEPRECATED_placeholder: 'Acme SAS',
        icon: Building2,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterAddress',
        type: 'address',
        icon: MapPin,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterPostcode',
        colSpan: 4 as const,
        icon: Hash,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterCity',
        type: 'city',
        colSpan: 8 as const,
        icon: Building2,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyCreationDate',
        icon: CalendarDays,
        showWhen: showWhenCompanyBeingCreated,
      },
      { name: 'companyRevenue', icon: Euro },
      { name: 'companyActivity', searchable: true, icon: BriefcaseIcon },
    ],
  },
];

const contactFunnelSections: FormSectionsWithShortcuts = [
  {
    title: 'Informations du contact',
    fields: [
      {
        name: 'contactCivility',
        icons: { MONSIEUR: User, MADAME: User, MADEMOISELLE: User },
      },
      { name: 'contactFirstName', colSpan: 6 as const, icon: User },
      { name: 'contactLastName', colSpan: 6 as const, icon: User },
      { name: 'contactEmail', colSpan: 6 as const, icon: Mail },
      {
        name: 'contactPhoneNumber',
        colSpan: 6 as const,
        icon: Phone,
        DEPRECATED_placeholder: '6 00 00 00 00',
      },
      { name: 'contactBirthDate', colSpan: 4 as const, icon: CalendarDays },
      {
        name: 'contactBirthCity',
        type: 'city',
        addressGroup: 'contactBirth',
        colSpan: 4 as const,
        icon: MapPin,
      },
      {
        name: 'contactBirthCountry',
        type: 'country',
        addressGroup: 'contactBirth',
        colSpan: 4 as const,
        icon: Globe,
      },
    ],
  },
];

const assetFunnelSections: FieldShortcut[] = [
  { name: 'assetAddress', icon: MapPin, type: 'address' },
  {
    name: 'assetPostalCode',
    colSpan: 4 as const,
    icon: Hash,
    additionalValidationRules: schema =>
      schema.refine(value => /^(?!20|97|98)/.test(String(value)), {
        message: 'Le bien ne peut pas être situé dans un département de Corse ou dans les DROM-COM',
      }),
  },
  { name: 'assetCity', type: 'city', colSpan: 8 as const, icon: Building2 },
  { name: 'assetSurface', icon: Maximize2 },
  {
    name: 'assetOccupantStatus',
    icons: {
      OWNER: Home,
      RENTER: Key,
      RENT_FREE_OCCUPANT: User,
      SUBLET: Users,
      TENANT_FOR_OWNER: Key,
    },
    perRow: 3,
  },
  { name: 'assetContentValue', icon: Euro, DEPRECATED_placeholder: '50 000' },
];

const KILLING_MESSAGE = 'Il est impossible de souscrire une police dans ce cas';

const killingQuestionsSections: FormSectionsWithShortcuts = [
  {
    title: "Déclarations de l'assuré",
    fields: [
      {
        name: 'kqNotInsuredWithHiscox',
        display: 'toggle' as const,
        icon: ShieldCheck,
        blockingValue: 'true',
        blockingErrorMessage: KILLING_MESSAGE,
        additionalValidationRules: (schema: ZodTypeAny) =>
          schema.and(
            z.literal('false', {
              errorMap: () => ({ message: KILLING_MESSAGE }),
            }),
          ),
      },
      {
        name: 'kqNoClaimsHistory',
        display: 'toggle' as const,
        icon: AlertTriangle,
        blockingValue: 'true',
        blockingErrorMessage: KILLING_MESSAGE,
        additionalValidationRules: (schema: ZodTypeAny) =>
          schema.and(
            z.literal('false', {
              errorMap: () => ({ message: KILLING_MESSAGE }),
            }),
          ),
      },
      {
        name: 'kqNotInCorseOrDromCom',
        display: 'toggle' as const,
        icon: Globe,
        blockingValue: 'true',
        blockingErrorMessage: KILLING_MESSAGE,
        additionalValidationRules: (schema: ZodTypeAny) =>
          schema.and(
            z.literal('false', {
              errorMap: () => ({ message: KILLING_MESSAGE }),
            }),
          ),
      },
      {
        name: 'kqNotHistoricMonument',
        display: 'toggle' as const,
        icon: Building2,
        blockingValue: 'true',
        blockingErrorMessage: KILLING_MESSAGE,
        additionalValidationRules: (schema: ZodTypeAny) =>
          schema.and(
            z.literal('false', {
              errorMap: () => ({ message: KILLING_MESSAGE }),
            }),
          ),
      },
      {
        name: 'kqBuildingsHardConstruction',
        display: 'toggle' as const,
        icon: Building2,
        blockingValue: 'true',
        blockingErrorMessage: KILLING_MESSAGE,
        additionalValidationRules: (schema: ZodTypeAny) =>
          schema.and(
            z.literal('false', {
              errorMap: () => ({ message: KILLING_MESSAGE }),
            }),
          ),
      },
      {
        name: 'kqStorageCompliance',
        display: 'toggle' as const,
        icon: Store,
        blockingValue: 'true',
        blockingErrorMessage: KILLING_MESSAGE,
        additionalValidationRules: (schema: ZodTypeAny) =>
          schema.and(
            z.literal('false', {
              errorMap: () => ({ message: KILLING_MESSAGE }),
            }),
          ),
      },
      {
        name: 'kqSecurityCompliance',
        display: 'toggle' as const,
        icon: Shield,
        blockingValue: 'true',
        blockingErrorMessage: KILLING_MESSAGE,
        additionalValidationRules: (schema: ZodTypeAny) =>
          schema.and(
            z.literal('false', {
              errorMap: () => ({ message: KILLING_MESSAGE }),
            }),
          ),
      },
      {
        name: 'kqComputerEquipmentCompliance',
        display: 'toggle' as const,
        icon: ShieldCheck,
        blockingValue: 'true',
        blockingErrorMessage: KILLING_MESSAGE,
        additionalValidationRules: (schema: ZodTypeAny) =>
          schema.and(
            z.literal('false', {
              errorMap: () => ({ message: KILLING_MESSAGE }),
            }),
          ),
      },
      {
        name: 'kqNoHotWorkOrWoodwork',
        display: 'toggle' as const,
        icon: AlertTriangle,
        blockingValue: 'true',
        blockingErrorMessage: KILLING_MESSAGE,
        additionalValidationRules: (schema: ZodTypeAny) =>
          schema.and(
            z.literal('false', {
              errorMap: () => ({ message: KILLING_MESSAGE }),
            }),
          ),
      },
      {
        name: 'kqRevenueCompliance',
        display: 'toggle' as const,
        icon: Euro,
        blockingValue: 'true',
        blockingErrorMessage: KILLING_MESSAGE,
        additionalValidationRules: (schema: ZodTypeAny) =>
          schema.and(
            z.literal('false', {
              errorMap: () => ({ message: KILLING_MESSAGE }),
            }),
          ),
      },
      {
        name: 'kqActivityCompliance',
        display: 'toggle' as const,
        icon: BriefcaseIcon,
        blockingValue: 'true',
        blockingErrorMessage: KILLING_MESSAGE,
        additionalValidationRules: (schema: ZodTypeAny) =>
          schema.and(
            z.literal('false', {
              errorMap: () => ({ message: KILLING_MESSAGE }),
            }),
          ),
      },
    ],
  },
];

const additionalQuoteComponentsSections = [
  {
    title: 'Frais grossistes',
    forTypes: ['WHOLESALE'],
    fields: [
      {
        name: 'wholesaleAdditionalCommissionPercentage',
        required: true,
        type: 'number',
        colSpan: 6 as const,
        icon: Percent,
        DEPRECATED_placeholder: '10',
        apiDataAccessor: (data: PolicyDto) => {
          const wholesaleAdditionalCommissionAmountWithFeesPreTaxes = data?.quote?.components?.find(
            c => c.id === 'WHOLESALE_ADDITIONAL_COMMISSION',
          )?.premium.amountWithFeesPreTaxes;

          const premiumAmountPreFeesAndTaxes = data?.quote?.components
            ?.filter(c =>
              [
                'RCE',
                'RCOCC',
                'BUILDING',
                'FACILITIES',
                'CONTENT',
                'FINANCIAL_LOSS',
                'ASSISTANCE',
                'LEGAL_PROTECTION',
                'RVT',
                'GAREAT',
                'CATNAT',
                'FGTI',
              ].includes(c.id),
            )
            ?.reduce((sum, c) => sum + (c.premium.amountPreFeesAndTaxes ?? 0), 0);

          if (
            !wholesaleAdditionalCommissionAmountWithFeesPreTaxes ||
            !premiumAmountPreFeesAndTaxes ||
            premiumAmountPreFeesAndTaxes === 0
          )
            return 10;

          return Math.round(
            (wholesaleAdditionalCommissionAmountWithFeesPreTaxes / premiumAmountPreFeesAndTaxes) *
              100,
          );
        },
        formDataAccessor: (data: Record<string, unknown>) => ({
          wholesaleAdditionalCommissionComponent: {
            componentIds: [
              'RCE',
              'RCOCC',
              'BUILDING',
              'FACILITIES',
              'CONTENT',
              'FINANCIAL_LOSS',
              'ASSISTANCE',
              'LEGAL_PROTECTION',
              'RVT',
              'GAREAT',
              'CATNAT',
              'FGTI',
            ],
            id: 'WHOLESALE_ADDITIONAL_COMMISSION',
            type: 'FEE',
            chargeType: 'RECURRING',
            amountPropertiesFrom: ['amountPreFeesAndTaxes'],
            percentage: ((Number(data.wholesaleAdditionalCommissionPercentage) ?? 0) ||
              0) as number,
          } satisfies ManualQuotePercentageDto,
        }),
      },
      {
        name: 'wholesaleSubscriptionFee',
        required: false,
        type: 'number',
        colSpan: 6 as const,
        icon: Euro,
        DEPRECATED_placeholder: '150',
        apiDataAccessor: (data: PolicyDto) => {
          const wholesaleSubscriptionFee = data?.quote?.components?.find(
            c => c.id === 'WHOLESALE_SUBSCRIPTION_FEE',
          );

          return centsToEuros(wholesaleSubscriptionFee?.premium?.amountWithFeesAndTaxes);
        },
        formDataAccessor: (data: Record<string, unknown>) => ({
          wholesaleSubscriptionFeeComponent: {
            id: 'WHOLESALE_SUBSCRIPTION_FEE',
            type: 'FEE',
            chargeType: 'ONE_TIME',
            premium: {
              amountPreFeesAndTaxes: 0,
              totalFees: eurosToCents(data.wholesaleSubscriptionFee as number | undefined) ?? 0,
              totalTaxes: 0,
            },
          } satisfies ManualQuoteComponentDto,
        }),
      },
    ],
  },
  {
    title: 'Frais distributeurs (peuvent être remplis par Tutassur ou le distributeur)',
    forTypes: ['RETAIL'],
    fields: [
      {
        name: 'intermediaryCommission',
        required: false,
        type: 'number',
        colSpan: 6 as const,
        icon: Percent,
        DEPRECATED_placeholder: '10',
        apiDataAccessor: (data: PolicyDto) => {
          const intermediaryCommission = data?.quote?.components?.find(
            c => c.id === 'RETAIL_COMMISSION',
          );

          const premiumAmountPreFeesAndTaxes = data?.quote?.components
            ?.filter(c =>
              [
                'RCE',
                'RCOCC',
                'BUILDING',
                'FACILITIES',
                'CONTENT',
                'FINANCIAL_LOSS',
                'ASSISTANCE',
                'LEGAL_PROTECTION',
                'RVT',
                'GAREAT',
                'CATNAT',
                'FGTI',
              ].includes(c.id),
            )
            ?.reduce((sum, c) => sum + (c.premium.amountPreFeesAndTaxes ?? 0), 0);

          if (
            !intermediaryCommission ||
            premiumAmountPreFeesAndTaxes === undefined ||
            premiumAmountPreFeesAndTaxes === 0
          )
            return undefined;

          return Math.round(
            (intermediaryCommission.premium.amountWithFeesPreTaxes / premiumAmountPreFeesAndTaxes) *
              100,
          );
        },
        formDataAccessor: data => ({
          intermediaryCommissionPercentage: {
            componentIds: [
              'RCE',
              'RCOCC',
              'BUILDING',
              'FACILITIES',
              'CONTENT',
              'FINANCIAL_LOSS',
              'ASSISTANCE',
              'LEGAL_PROTECTION',
              'RVT',
              'GAREAT',
              'CATNAT',
              'FGTI',
            ],
            id: 'RETAIL_COMMISSION',
            type: 'FEE',
            chargeType: 'RECURRING',
            amountPropertiesFrom: ['amountPreFeesAndTaxes'],
            percentage: (data.intermediaryCommission ?? 0) as number,
          } satisfies ManualQuotePercentageDto,
          wholesaleAdditionalCommissionComponent: {
            componentIds: [
              'RCE',
              'RCOCC',
              'BUILDING',
              'FACILITIES',
              'CONTENT',
              'FINANCIAL_LOSS',
              'ASSISTANCE',
              'LEGAL_PROTECTION',
              'RVT',
              'GAREAT',
              'CATNAT',
              'FGTI',
            ],
            id: 'WHOLESALE_ADDITIONAL_COMMISSION',
            type: 'FEE',
            chargeType: 'RECURRING',
            amountPropertiesFrom: ['amountPreFeesAndTaxes'],
            percentage: 10,
          } satisfies ManualQuotePercentageDto,
        }),
      },
      {
        name: 'retailAnnualFee',
        required: false,
        type: 'number',
        colSpan: 6 as const,
        icon: Euro,
        DEPRECATED_placeholder: '80',
        apiDataAccessor: (data: PolicyDto) => {
          const renewalFee = data?.quote?.components?.find(c => c.id === 'RETAIL_ANNUAL_FEE');
          return centsToEuros(renewalFee?.premium?.totalFees);
        },
        formDataAccessor: (data: Record<string, unknown>) => {
          const renewalFee = data.retailAnnualFee as number | undefined;
          if (!renewalFee) return {};
          return {
            renewalFeeComponent: {
              id: 'RETAIL_ANNUAL_FEE',
              type: 'FEE',
              chargeType: 'SPOT',
              premium: {
                amountPreFeesAndTaxes: 0,
                totalFees: eurosToCents(renewalFee) ?? 0,
                totalTaxes: 0,
              },
            } satisfies ManualQuoteComponentDto,
          };
        },
      },
      {
        name: 'retailSubscriptionFee',
        required: false,
        type: 'number',
        icon: Euro,
        DEPRECATED_placeholder: '100',
        apiDataAccessor: (data: PolicyDto) => {
          const retailSubscriptionFee = data?.quote?.components?.find(
            c => c.id === 'RETAIL_SUBSCRIPTION_FEE',
          );

          return centsToEuros(retailSubscriptionFee?.premium?.amountWithFeesAndTaxes);
        },
        formDataAccessor: (data: Record<string, unknown>) => {
          const totalFees = data.retailSubscriptionFee as number | undefined;

          if (!totalFees) return {};

          return {
            intermediarySubscriptionFeeComponent: {
              id: 'RETAIL_SUBSCRIPTION_FEE',
              type: 'FEE',
              chargeType: 'ONE_TIME',
              premium: {
                amountPreFeesAndTaxes: 0,
                totalFees: eurosToCents(totalFees) ?? 0,
                totalTaxes: 0,
              },
            } satisfies ManualQuoteComponentDto,
          };
        },
      },
    ],
  },
] as const satisfies Array<FormSection & { forTypes?: string[] }>;

const BILLING_FREQUENCY_ICONS = {
  MONTHLY: CalendarDays,
  QUARTERLY: CalendarRange,
  HALF_YEARLY: CalendarClock,
  YEARLY: CalendarCheck,
};

const policyHolderInvitationRole = ['DRIVER'];

export const tutassurHiscoxMultirisquesProConfig = {
  display: {
    category: 'professionnel',
    icon: BriefcaseIcon,
    badges: ['online'],
  },
  productCustomPath: '/multirisques-pro-tarif-en-ligne',
  discoveryFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'documents-upload',
        type: 'documentUploadFunnel' as const,
        context: 'policy' as const,
        icon: FileUp,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: Building2,
        sections: companyFunnelSections,
      },
      {
        path: 'asset',
        type: 'assetFunnel',
        icon: Store,
        maxAssets: 1,
        fields: assetFunnelSections,
      },
      {
        path: 'contact',
        type: 'customerFunnel',
        icon: User,
        sections: contactFunnelSections,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },
      {
        path: 'killing-questions',
        type: 'policyFunnel',
        icon: ShieldAlert,
        sections: killingQuestionsSections,
      },
      {
        path: 'billing',
        type: 'billingSelectionFunnel',
        icon: CalendarClock,
        frequencyIcons: BILLING_FREQUENCY_ICONS,
      },
      {
        path: 'quote',
        type: 'quoteFunnel',
        icon: Shield,
        policySections: [
          {
            title: 'Franchise',
            fields: [
              {
                name: 'customExcess',
                required: true,
                type: 'hiscoxMrpExcess',
                icon: Euro,
                options: [
                  { value: '250', label: '250€' },
                  { value: '300', label: '300€' },
                  { value: '400', label: '400€' },
                  { value: '500', label: '500€' },
                  { value: '750', label: '750€' },
                  { value: '1000', label: '1000€' },
                  { value: '1500', label: '1500€' },
                  { value: '2000', label: '2000€' },
                  { value: '2500', label: '2500€' },
                  { value: '3000', label: '3000€' },
                  { value: '5000', label: '5000€' },
                  { value: '10000', label: '10000€' },
                ],
                isCustomField: true,
              },
            ],
          },
        ],
        assetSections: [
          {
            title: "Date de prise d'effet de l'assurance",
            fields: [
              {
                name: 'startedAt',
                required: true,
                type: 'datetime',
                min: 'now',
                icon: CalendarDays,
              },
            ],
          },
        ],
        additionalQuoteComponentsSections,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'new-business-end',
        icon: PartyPopper,
        callback: {
          callToActionCallback: async ({ policyId, branchId }) => {
            await policyControllerPushPolicyEvent(
              policyId,
              { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
              { branchId },
            );
          },
        },
        additionalComponents: {
          isQuoteTierModuleVisible: true,
        },
      },
    ],
  },
  mtaFunnelSteps: {
    steps: [
      {
        path: 'new',
        type: 'newMTACreation',
        icon: FilePen,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: Building2,
        sections: companyFunnelSections,
      },
      {
        path: 'asset',
        type: 'assetFunnel',
        icon: Store,
        maxAssets: 1,
        fields: assetFunnelSections,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },
      {
        path: 'killing-questions',
        type: 'policyFunnel',
        icon: ShieldAlert,
        sections: killingQuestionsSections,
      },
      {
        path: 'quote',
        type: 'quoteFunnel',
        icon: Shield,
        policySections: [],
        assetSections: [],
        additionalQuoteComponentsSections,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'mta-end',
        icon: PartyPopper,
        callback: {},
        additionalComponents: {
          isQuoteTierModuleVisible: true,
        },
      },
    ],
  },
  customerInformations: {
    fieldsConfig: [
      { key: 'companyName', contactKey: ContactKey.COMPANY_NAME },
      'companyHeadquarterAddress',
      'companyHeadquarterPostcode',
      'companyHeadquarterCity',
      'companySiret',
      'companyNafCode',
      'companyRevenue',
      'companyActivity',
      'isCompanyBeingCreated',
      { key: 'contactFirstName', contactKey: ContactKey.FIRST_NAME },
      { key: 'contactLastName', contactKey: ContactKey.LAST_NAME },
      'contactEmail',
      'contactPhoneNumber',
      'contactCivility',
      'contactBirthDate',
      'contactBirthCity',
      'contactBirthCountry',
    ],
  },
  assetInformations: {
    fieldsConfig: [
      'assetAddress',
      'assetPostalCode',
      'assetCity',
      'assetSurface',
      'assetOccupantStatus',
      'assetContentValue',
    ],
  },
  quoteTierSelectModuleConfig: {
    customIncludedPerils: IncludedPerilsMRP,
  },
  quoteTierModuleConfig: {
    goToTiersOnMta: '/mta/multirisques-pro-tarif-en-ligne/${policy.customer}/${policy.id}/new',
    goToTiersOnNewBusiness:
      '/discovery/multirisques-pro-tarif-en-ligne/${policy.customer}/${policy.id}/${workingBranch?.id}/quote',
    fieldsConfig: [],
    perilsMapping: {},
    isExternalQuoteEnabled: false,
    isChosenTierDisplayed: true,
  },
  priceBreakdownConfig: {
    groups: [
      { lines: [{ key: 'TOTAL_PREMIUM', variant: 'dimmed' }] },
      { lines: [{ key: 'BROKERAGE_FEES' }] },
      { lines: [{ key: 'SUBSCRIPTION_FEE' }, { key: 'ANNUAL_FEE', tooltip: true }] },
      { lines: [{ key: 'FGTI_TAX', tooltip: true }] },
    ],
  },
  brokerPolicyDashboardHome: {
    isFinalizeNewBusinessModuleVisible: true,
    isAuthorCardModuleVisible: true,
    isPolicyholderInvitationModuleVisible: true,
    isQuoteTierModuleVisible: true,
    isPremiumModuleVisible: true,
    isPolicyLifecycleSummaryModuleVisible: true,
    isInvoicesBalanceModuleVisible: true,
    customModules: [],
  },
  additionalDocumentTypes: [
    'PSC_SPECIFIC_CONDITIONS',
    'MRP_SPECIFIC_CONDITIONS',
    'RCE_SPECIFIC_CONDITIONS',
    'QUOTE_PSC',
    'QUOTE_MRP',
    'QUOTE_RCE',
  ],
  policyholderInvitationModuleConfig: {
    relationTypes: policyHolderInvitationRole,
  },
  finalizeNewBusinessModuleConfig: {
    buttons: [
      {
        id: 'send-via-email',
        actionType: 'signature',
        visual: {
          label: 'Envoi du contrat au client',
          icon: 'mail',
          successMessage: 'Contrat envoyé par email',
        },
        signature: {
          signatureType: 'direct',
          deliveryMode: 'EMAIL',
          otp: {
            enabled: false,
          },
        },
        visibilityConditions: {
          policyStatuses: ['POLICY_QUOTED'],
        },
      },
    ],
    rules: [
      {
        id: 'documents-import',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ policy }) => policy.customFields.allClientDocumentsOk === true,
      },
      {
        id: 'sepa-payment',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/payment-methods`,
        isValid: ({ paymentMethods }) =>
          paymentMethods.some(({ paymentMethodType }) => paymentMethodType === 'sepa_debit'),
      },
    ],
  },
  policyDashboardEventsModuleConfig: {
    enableAssetComparison: true,
    enablePolicyComparison: false,
  },
} satisfies KorintModulesProductsConfig;
